import { useEffect, useState } from "react";
import { Paper, Box, Button, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { getMaxEndDate, getMinStartDate, isDateRangeInvalid } from "../constants";
import { exportLeadsToCSV } from "../leadsCsvHelpers";

interface LeadsFilterCardProps {
  data: LeadsManagementData;
}

function toDayjs(value: string): Dayjs | null {
  return value ? dayjs(value) : null;
}

function toIsoDate(value: Dayjs | null): string {
  return value && value.isValid() ? value.format("YYYY-MM-DD") : "";
}

// DatePicker's own `readOnly` prop disables the popup calendar too, which
// isn't what we want — only keyboard entry should be blocked, selecting via
// the calendar must keep working. Tab is left alone so focus can still move.
function blockTyping(event: React.KeyboardEvent) {
  if (event.key !== "Tab") event.preventDefault();
}

function blockPaste(event: React.ClipboardEvent) {
  event.preventDefault();
}

export default function LeadsFilterCard({ data }: LeadsFilterCardProps) {
  const { leadsForStatus, refetch, isLoading, start, end, setStart, setEnd } = data;

  // Selecting a date used to filter immediately (start/end are the actual
  // request params, so setStart/setEnd triggered a fetch on every change).
  // Now that there's a "Get Data" button, picking a date only updates this
  // local draft — the real start/end (and the fetch they trigger) only
  // change when Get Data is clicked. Synced back from start/end via the
  // effects below so external date changes (e.g. "See all leads from this
  // IP" elsewhere calling goToFilteredLeads) still show up here.
  const [draftStart, setDraftStart] = useState(start);
  const [draftEnd, setDraftEnd] = useState(end);

  useEffect(() => setDraftStart(start), [start]);
  useEffect(() => setDraftEnd(end), [end]);

  const today = dayjs();
  // getMaxEndDate only caps the 365-day range window — it says nothing about
  // "today", so without this the End date picker let you pick a date in the
  // future. Whichever is earlier (the range cap or today) wins — same rule
  // as Analytics' AnalyticsFilterItems.
  const rangeMaxEndDate = toDayjs(getMaxEndDate(draftStart) ?? "");
  const maxEndDate = rangeMaxEndDate && rangeMaxEndDate.isBefore(today) ? rangeMaxEndDate : today;
  const minStartDate = getMinStartDate(draftEnd);

  // A change that would push the range over 365 days (or invert it) is
  // rejected outright — the other date must be adjusted first, it is never
  // auto-adjusted for the user. Typing is blocked on both pickers (see
  // blockTyping/blockPaste below) so this mainly guards picker selections,
  // but it stays as a defensive backstop.
  function handleStartChange(nextStart: string) {
    if (nextStart && isDateRangeInvalid(nextStart, draftEnd)) return;
    setDraftStart(nextStart);
  }

  function handleEndChange(nextEnd: string) {
    if (nextEnd && isDateRangeInvalid(draftStart, nextEnd)) return;
    if (nextEnd && dayjs(nextEnd).isAfter(today, "day")) return;
    setDraftEnd(nextEnd);
  }

  function handleGetData() {
    setStart(draftStart);
    setEnd(draftEnd);
  }

  return (
    <Paper
      sx={{
        mt: 2,
        boxShadow: "none",
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "flex-end",
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="Start Date"
          format="DD-MM-YYYY"
          value={toDayjs(draftStart)}
          onChange={(value) => handleStartChange(toIsoDate(value))}
          minDate={toDayjs(minStartDate ?? "") ?? undefined}
          maxDate={toDayjs(draftEnd) ?? today}
          disabled={isLoading}
          slotProps={{
            textField: {
              size: "small",
              color: "secondary",
              onKeyDown: blockTyping,
              onPaste: blockPaste,
              "data-tracker": "923",
              sx: { width: 273 },
            } as any,
            popper: { disablePortal: true },
          }}
        />

        <DatePicker
          label="End Date"
          format="DD-MM-YYYY"
          value={toDayjs(draftEnd)}
          onChange={(value) => handleEndChange(toIsoDate(value))}
          minDate={toDayjs(draftStart) ?? undefined}
          maxDate={maxEndDate}
          disabled={isLoading}
          slotProps={{
            textField: {
              size: "small",
              color: "secondary",
              onKeyDown: blockTyping,
              onPaste: blockPaste,
              "data-tracker": "924",
              sx: { width: 273 },
            } as any,
            popper: { disablePortal: true },
          }}
        />
      </LocalizationProvider>

      <Button
        variant="contained"
        onClick={handleGetData}
        disabled={isLoading}
        data-tracker="1042"
        size="large"
        sx={{
          bgcolor: "secondary.main",
          "&:hover": { bgcolor: "secondary.dark" },
          borderRadius: 1,
          fontWeight: 500,
          letterSpacing: "0.46px",
        }}
      >
        Get Data
      </Button>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, pb: 0.5, ml: "auto" }}>
        <Tooltip title="Refresh">
          <IconButton size="large" onClick={refetch} disabled={isLoading} data-tracker="926" sx={{ color: "secondary.main" }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export">
          <IconButton
            size="large"
            onClick={() => exportLeadsToCSV({ leads: leadsForStatus })}
            disabled={isLoading}
            data-tracker="927"
            sx={{ color: "secondary.main" }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
}
